import { useId, useState } from "react"
import { ALL_INDIA, STATES, sideWords, sourceWords, stateName, type Side, type ThreadContext } from "../readerContext"
import { usePopover } from "./usePopover"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { GateLine, gateAction } from "./gate"

interface ContextMenuProps {
  ctx: ThreadContext
  onChange: (ctx: ThreadContext) => void
  /** A line under the choices: on a thread, that the next question uses them. */
  note?: string
  /** Re-run the thread's latest question with the new choices; absent when there is nothing to re-run. */
  onRerun?: () => void
  /** An answer is running: re-running waits. */
  rerunBusy?: boolean
}

/** Said once, the first time a reader with no State opens the chip (handoff 3 §11). */
const WHY_KEY = "nira.state-why-seen"
const seenWhy = () => { try { return localStorage.getItem(WHY_KEY) === "1" } catch { return true } }

/** The context chip, "Telangana · for a taxpayer ▾", and its menu: the State and the side. The only place the side is set. */
export function ContextMenu({ ctx, onChange, note, onRerun, rerunBusy }: ContextMenuProps) {
  const { open, setOpen, button, panel } = usePopover()
  const id = useId()
  // No State yet: the chip asks for one, and the first opening says why it matters.
  const unset = ctx.state_code === ALL_INDIA
  const [explain, setExplain] = useState(false)
  // A choice was changed since the last answer: offer to re-run the last question with it.
  const [changed, setChanged] = useState(false)
  const toggle = () => {
    if (!open && unset && !seenWhy()) {
      setExplain(true)
      try { localStorage.setItem(WHY_KEY, "1") } catch { /* private window: said again next time */ }
    }
    setOpen((v) => !v)
  }
  const place = ctx.state || stateName(ctx.state_code) || "Your State"
  const pick = (next: Partial<ThreadContext>) => {
    setChanged(true)
    onChange({
      ...ctx,
      ...next,
      state: next.state_code ? stateName(next.state_code) : ctx.state,
      state_source: next.state_code ? "chosen" : ctx.state_source,
    })
  }
  const why = sourceWords(ctx)
  // Reading for the department is metered (one free, then Pro): the paywall is on this switch, before any turn runs.
  const [sideLine, setSideLine] = useState("")
  const chooseSide = (side: Side) => {
    if (side !== "revenue" || ctx.side === "revenue") { setSideLine(""); pick({ side }); return }
    setSideLine("")
    void gateAction("nira.department_reading", { trigger: 10 }).then((g) => {
      if (g.ok) pick({ side })
      else setSideLine(g.line)
    })
  }
  return (
    <div className="nr-ctx">
      <button
        ref={button}
        type="button"
        className="nr nr-chip"
        data-side={ctx.side === "revenue" ? "department" : "taxpayer"}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        aria-label={`Answering ${sideWords(ctx.side)} in ${place}. Change`}
        onClick={toggle}
      >
        <span>{place}</span>
        <span className="nr-chip-dot" aria-hidden="true">·</span>
        <span className={unset ? "nr-chip-ask" : undefined}>{unset ? "set your State" : sideWords(ctx.side)}</span>
        <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5" /></svg>
      </button>
      {open ? (
        <div ref={panel} className="nr nr-menu" id={`${id}-menu`} role="dialog" aria-label="Who this is for">
          <label className="nr-field">
            <span className="nr-field-label">Your State</span>
            <select className="nr-input" value={ctx.state_code} onChange={(e) => pick({ state_code: e.target.value })}>
              {STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </label>
          {explain ? <p className="nr-menu-why">A High Court binds its own State, so its decisions weigh first. Set yours and Nira reads them that way.</p> : why ? <p className="nr-menu-why">{why}</p> : null}
          <fieldset className="nr-menu-sides">
            <legend className="nr-field-label">Read it</legend>
            {(["taxpayer", "revenue"] as Side[]).map((side) => (
              <label key={side} className="nr-radio">
                <input type="radio" name={`${id}-side`} checked={ctx.side === side} onChange={() => chooseSide(side)} />
                <span>{sideWords(side)}</span>
              </label>
            ))}
          </fieldset>
          <AllowanceHint metric="nira.department_reading" />
          <GateLine text={sideLine} />
          {note ? <p className="nr-menu-note">{onRerun ? `${note} Re-run to apply it to this one.` : note}</p> : null}
          {onRerun && changed ? (
            <button
              type="button"
              className="nr-menu-rerun"
              disabled={rerunBusy}
              onClick={() => { setChanged(false); setOpen(false); onRerun() }}
            >
              Re-run the last question with this
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
